import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { TILE_CONFIG } from "@/lib/mapConfig";

// Centro inicial: zona El Cerrito / Ginebra, Valle del Cauca (piloto). Solo la vista; no fija puntos.
const INITIAL_CENTER = [3.7050, -76.2900];

function pin(color, label) {
  return L.divIcon({
    className: "",
    html: `<div class="tf-pin" style="background:${color}"><span>${label}</span></div>`,
    iconSize: [30, 30],
    iconAnchor: [4, 28],
  });
}

// mode: which point the next map click sets ("pickup" | "dropoff")
// route: GeoJSON coords [lng,lat][] — real road geometry from OSRM (optional)
// onMarkerMove: (which, {lat,lng}) when user drags a marker to correct location
export const MapPicker = ({ pickup, dropoff, mode, route, onPick, onMarkerMove }) => {
  const el = useRef(null);
  const map = useRef(null);
  const markers = useRef({ pickup: null, dropoff: null });
  const line = useRef(null);
  const onPickRef = useRef(onPick);
  const onMarkerMoveRef = useRef(onMarkerMove);
  const modeRef = useRef(mode);
  onPickRef.current = onPick;
  onMarkerMoveRef.current = onMarkerMove;
  modeRef.current = mode;

  useEffect(() => {
    if (map.current || !el.current) return;
    const m = L.map(el.current, { zoomControl: true, attributionControl: true }).setView(INITIAL_CENTER, 12);
    L.tileLayer(TILE_CONFIG.url, {
      maxZoom: 19,
      subdomains: TILE_CONFIG.subdomains,
      attribution: TILE_CONFIG.attribution,
      className: TILE_CONFIG.needsDarkFilter ? "tf-dark-tiles" : "",
    }).addTo(m);
    // Asegura el filtro oscuro a nivel contenedor (además del pane) cuando el proveedor es OSM
    if (TILE_CONFIG.needsDarkFilter) m.getContainer().classList.add("tf-dark-tiles");
    m.on("click", (e) => {
      const which = modeRef.current;
      onPickRef.current?.(which, { lat: +e.latlng.lat.toFixed(6), lng: +e.latlng.lng.toFixed(6) });
    });
    map.current = m;
    setTimeout(() => m.invalidateSize(), 200);
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const draw = (key, point, color, label) => {
      if (markers.current[key]) {
        m.removeLayer(markers.current[key]);
        markers.current[key] = null;
      }
      if (point?.lat != null) {
        const mk = L.marker([point.lat, point.lng], { icon: pin(color, label), draggable: true }).addTo(m);
        mk.on("dragend", () => {
          const ll = mk.getLatLng();
          onMarkerMoveRef.current?.(key, { lat: +ll.lat.toFixed(6), lng: +ll.lng.toFixed(6) });
        });
        markers.current[key] = mk;
      }
    };
    draw("pickup", pickup, "#FF6A00", "A");
    draw("dropoff", dropoff, "#34D399", "B");
    if (line.current) {
      m.removeLayer(line.current);
      line.current = null;
    }
    if (route && route.length > 1) {
      // ruta real por calles (geometría OSRM)
      const latlngs = route.map(([lng, lat]) => [lat, lng]);
      line.current = L.polyline(latlngs, { color: "#FF6A00", weight: 4, opacity: 0.9 }).addTo(m);
      m.fitBounds(line.current.getBounds(), { padding: [50, 50] });
    } else if (pickup?.lat != null && dropoff?.lat != null) {
      line.current = L.polyline(
        [
          [pickup.lat, pickup.lng],
          [dropoff.lat, dropoff.lng],
        ],
        { color: "#FF6A00", weight: 3, dashArray: "6 8", opacity: 0.8 }
      ).addTo(m);
      m.fitBounds(line.current.getBounds(), { padding: [50, 50] });
    } else if (pickup?.lat != null) {
      m.setView([pickup.lat, pickup.lng], 17);
    } else if (dropoff?.lat != null) {
      m.setView([dropoff.lat, dropoff.lng], 17);
    }
  }, [pickup, dropoff, route]);

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-[#27272A] shadow-inner">
      <div ref={el} className="h-[300px] w-full sm:h-[380px]" data-testid="map-picker" />
      <div className="pointer-events-none absolute left-3 top-3 z-[500] max-w-[85%] rounded-full bg-black/70 px-3 py-1.5 text-xs font-bold text-white backdrop-blur">
        Busca arriba o toca el mapa:{" "}
        <span style={{ color: mode === "pickup" ? "#FF6A00" : "#34D399" }}>
          {mode === "pickup" ? "Origen (A)" : "Destino (B)"}
        </span>
        {" · arrastra los pines para corregir"}
      </div>
    </div>
  );
};
